import { type ToolSpec, n, s, fmt } from "./types";

export const HOMESTEAD_TOOLS: ToolSpec[] = [
  {
    slug: "raised-bed-soil-calculator",
    cluster: "homestead",
    name: "Raised Bed Soil Blend",
    h1: "Raised Garden Bed Soil Volume & Mix Ratio Calculator (Cubic Feet & Bags)",
    title: "Raised Bed Soil Calculator: Cu Ft & Bags | Free (2026)",
    description: "Calculate raised garden bed soil volume and Mel's Mix component bags instantly. Free calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "raised bed soil calculator cubic feet",
      "how many bags of compost peat moss vermiculite for raised bed",
      "mel's mix ratio calculator",
      "4x8 raised garden bed soil volume",
      "soil mix calculator for vegetable garden beds",
      "raised bed soil bags needed calculator",
    ],
    inputs: [
      { id: "length", label: "Bed length", type: "number", def: "8", unit: "ft", min: 0.1, step: 0.1 },
      { id: "width", label: "Bed width", type: "number", def: "4", unit: "ft", min: 0.1, step: 0.1 },
      { id: "depth", label: "Soil depth", type: "number", def: "12", unit: "in", min: 1, step: 0.5 },
      { id: "recipe", label: "Recipe", type: "select", def: "mel", options: [
        { value: "mel", label: "Mel's Mix (1/3 compost, 1/3 peat, 1/3 vermiculite)" },
        { value: "topsoil", label: "50/30/20 Topsoil / Compost / Amendment" },
      ] },
      { id: "bagsize", label: "Bag size for compost/peat/vermiculite", type: "number", def: "1.5", unit: "cu ft/bag", min: 0.1, step: 0.1 },
    ],
    compute: (v) => {
      const length = n(v, "length"), width = n(v, "width"), depth = n(v, "depth");
      const recipe = s(v, "recipe"), bagsize = n(v, "bagsize", 1.5);
      const totalCuFt = length * width * (depth / 12) * 1.10;
      const totalCuYd = totalCuFt / 27;
      const partA = recipe === "mel" ? totalCuFt / 3 : totalCuFt * 0.5;
      const partB = recipe === "mel" ? totalCuFt / 3 : totalCuFt * 0.3;
      const partC = recipe === "mel" ? totalCuFt / 3 : totalCuFt * 0.2;
      const labelA = recipe === "mel" ? "Compost" : "Topsoil";
      const labelB = recipe === "mel" ? "Peat Moss / Coco Coir" : "Compost";
      const labelC = recipe === "mel" ? "Coarse Vermiculite" : "Amendment (perlite/manure)";
      const bagsA = bagsize > 0 ? Math.ceil(partA / bagsize) : 0;
      const bagsB = bagsize > 0 ? Math.ceil(partB / bagsize) : 0;
      const bagsC = bagsize > 0 ? Math.ceil(partC / bagsize) : 0;
      return {
        cards: [
          { label: "Total soil volume", value: `${fmt(totalCuFt, 1)} cu ft`, sub: `${fmt(totalCuYd, 2)} cu yd`, accent: true },
          { label: labelA, value: `${fmt(partA, 1)} cu ft`, sub: `${bagsA} bags` },
          { label: labelB, value: `${fmt(partB, 1)} cu ft`, sub: `${bagsB} bags` },
          { label: labelC, value: `${fmt(partC, 1)} cu ft`, sub: `${bagsC} bags` },
        ],
        table: {
          title: "Soil mix breakdown",
          head: ["Component", "Volume (cu ft)", "Bags"],
          rows: [
            [labelA, fmt(partA, 1), bagsA],
            [labelB, fmt(partB, 1), bagsB],
            [labelC, fmt(partC, 1), bagsC],
            ["Total (10% compaction buffer included)", fmt(totalCuFt, 1), bagsA + bagsB + bagsC],
          ],
        },
        summary: `Your ${fmt(length, 1)}×${fmt(width, 1)}ft bed at ${fmt(depth, 0)}in deep needs ${fmt(totalCuFt, 1)} cu ft (${fmt(totalCuYd, 2)} cu yd) of soil mix.`,
      };
    },
    intro: "Guessing at raised bed soil quantity leads to either an expensive surplus of bagged compost or a frustrating mid-project run back to the garden center. This calculator converts your bed's exact dimensions into cubic feet and splits the total into the classic Mel's Mix or a topsoil-based recipe.",
    sections: [
      { h: "Why the 10% compaction buffer matters", body: "Loose bagged soil settles significantly once watered and walked around, often dropping 5-10% in volume within the first season. Building in that buffer up front means you won't be topping off the bed every few weeks." },
      { h: "Mel's Mix vs topsoil-based recipes", body: "Mel's Mix (equal parts compost, peat moss, and vermiculite) is lightweight, drains well, and is the Square Foot Gardening standard. A 50/30/20 topsoil blend is cheaper at scale and holds moisture longer, better suited to larger beds and drought-prone climates." },
      { h: "Converting cubic feet to bags", body: "Most compost, peat, and vermiculite bags are sold in 1.5, 2, or 3 cubic foot sizes — always check the bag label and adjust the bag-size input so the bag counts match what's actually on the shelf." },
      { h: "Deeper beds for root crops", body: "Standard leafy greens and herbs are fine at 8-10 inches, but carrots, potatoes, and tomatoes benefit from 12-18 inches of depth — increase the depth input accordingly for a more accurate volume." },
    ],
    staticTable: {
      title: "Common raised bed sizes and soil needs",
      head: ["Bed size", "Depth", "Approx. cu ft (with buffer)"],
      rows: [
        ["4x4 ft", "8 in", "~11.7"],
        ["4x8 ft", "12 in", "~35.2"],
        ["3x6 ft", "10 in", "~16.5"],
        ["2x8 ft", "8 in", "~11.7"],
        ["8x8 ft", "12 in", "~70.4"],
        ["4x4 ft", "12 in", "~17.6"],
      ],
    },
    faqs: [
      { q: "How much soil do I need for a 4x8 raised bed?", a: "At 12 inches deep with a 10% compaction buffer, a 4x8 bed needs roughly 35 cubic feet of soil mix, split into about 12 cubic feet each of compost, peat moss, and vermiculite for Mel's Mix." },
      { q: "Can I fill the bottom with cheaper filler material?", a: "Yes — many gardeners use a hugelkultur base of logs, branches, or straw in the bottom third of deep beds to reduce soil cost, but only for beds 18+ inches deep to leave enough root zone above." },
      { q: "Is Mel's Mix better than regular garden soil?", a: "Mel's Mix drains faster and resists compaction better than plain topsoil, making it ideal for raised beds, but it also dries out faster and needs more frequent watering." },
      { q: "What can I use instead of vermiculite?", a: "Perlite is a common substitute with similar water-retention and aeration properties, though it floats more in heavy rain; coarse sand works in a pinch but adds significant weight." },
      { q: "How often do I need to top off a raised bed?", a: "Expect to add 1-3 inches of fresh compost annually as organic matter breaks down and volume settles, even with the initial compaction buffer built in." },
      { q: "Do I need different amounts for a round or oddly shaped bed?", a: "Calculate the footprint area of your specific shape in square feet, then multiply by depth in feet to get cubic feet — this rectangular calculator is a stand-in for that same base math." },
      { q: "Is coco coir a good peat moss substitute?", a: "Yes, coco coir performs similarly for water retention and is considered more sustainably harvested than peat moss, and it can be substituted 1:1 by volume." },
      { q: "How many bags of compost equal one cubic yard?", a: "One cubic yard equals 27 cubic feet, so at a common 1.5 cu ft bag size, you'd need 18 bags to fill one cubic yard." },
      { q: "Should I use garden soil or potting mix in raised beds?", a: "Neither alone is ideal — dense garden soil compacts too much and pure potting mix dries out too fast, which is why blended recipes like Mel's Mix or the 50/30/20 ratio exist." },
      { q: "What depth is minimum for growing tomatoes in a raised bed?", a: "Tomatoes need at least 12-18 inches of loose soil depth to develop a healthy root system and support fruit production." },
    ],
    formula: {
      expressions: [
        "TotalCuFt = Length_ft × Width_ft × (Depth_in / 12) × 1.10",
        "ComponentVolume = TotalCuFt / 3 (Mel's Mix, equal thirds)",
        "Bags = ceil(ComponentVolume / BagSize_cuft)",
      ],
      variables: [
        { name: "1.10", meaning: "10% compaction buffer" },
        { name: "BagSize", meaning: "Cubic feet per bag purchased" },
      ],
    },
    related: ["pasture-grazing-aum-calculator", "worm-composting-calculator", "beekeeping-sugar-syrup-calculator"],
  },

  {
    slug: "pasture-grazing-aum-calculator",
    cluster: "homestead",
    name: "Pasture Grazing AUM",
    h1: "Livestock Pasture Grazing Capacity & Animal Unit Month (AUM) Sizer",
    title: "Pasture Grazing AUM Calculator | Free (2026)",
    description: "Calculate livestock carrying capacity and stocking density instantly. Free pasture grazing AUM calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "pasture grazing aum calculator",
      "how many cows per acre calculator",
      "animal unit month carrying capacity calculator",
      "rotational grazing stocking density calculator",
      "pasture forage yield calculator",
      "overgrazing risk calculator livestock",
    ],
    inputs: [
      { id: "acres", label: "Total pasture acreage", type: "number", def: "20", unit: "acres", min: 0.1, step: 0.1 },
      { id: "forage", label: "Forage production", type: "number", def: "2500", unit: "lbs DM/acre", min: 1, step: 10 },
      { id: "animal", label: "Animal type", type: "select", def: "cow", options: [
        { value: "cow", label: "Beef cow with calf (1.0 AU)" },
        { value: "bull", label: "Bull (1.25 AU)" },
        { value: "sheep", label: "Sheep (0.2 AU)" },
        { value: "horse", label: "Horse (1.2 AU)" },
      ] },
      { id: "head", label: "Number of head", type: "number", def: "10", min: 1, step: 1 },
      { id: "months", label: "Grazing season duration", type: "number", def: "6", unit: "months", min: 0.5, step: 0.5 },
    ],
    compute: (v) => {
      const acres = n(v, "acres"), forage = n(v, "forage"), animal = s(v, "animal");
      const head = n(v, "head"), months = n(v, "months");
      const auFactor: Record<string, number> = { cow: 1.0, bull: 1.25, sheep: 0.2, horse: 1.2 };
      const au = auFactor[animal] ?? 1.0;
      const totalForage = acres * forage;
      const usableForage = totalForage * 0.5;
      const standardAUM = 780;
      const carryingCapacityAU = months > 0 ? usableForage / (standardAUM * months) : 0;
      const carryingCapacityHead = au > 0 ? carryingCapacityAU / au : 0;
      const currentAU = head * au;
      const acresPerAU = currentAU > 0 ? acres / currentAU : 0;
      const overgrazing = currentAU > carryingCapacityAU;
      return {
        cards: [
          { label: "Total available forage", value: `${fmt(totalForage, 0)} lbs DM`, sub: "Before take-half-leave-half" },
          { label: "Usable forage", value: `${fmt(usableForage, 0)} lbs DM`, sub: "50% harvest rule" },
          { label: "Carrying capacity", value: `${fmt(carryingCapacityHead, 1)} head`, sub: `${fmt(carryingCapacityAU, 1)} animal units` , accent: true },
          { label: "Stocking density", value: `${fmt(acresPerAU, 2)} acres/AU`, sub: overgrazing ? "⚠ Overgrazing risk" : "Within safe capacity" },
        ],
        table: {
          title: "Grazing capacity breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Total pasture acreage", `${fmt(acres, 1)} acres`],
            ["Forage yield", `${fmt(forage, 0)} lbs DM/acre`],
            ["Usable forage (50%)", `${fmt(usableForage, 0)} lbs DM`],
            ["Grazing season", `${fmt(months, 1)} months`],
            ["Current stocking (AU)", fmt(currentAU, 2)],
            ["Max carrying capacity (AU)", fmt(carryingCapacityAU, 2)],
            ["Max head supported", fmt(carryingCapacityHead, 1)],
          ],
        },
        summary: overgrazing
          ? `Your ${fmt(head, 0)} head (${fmt(currentAU, 1)} AU) exceed the safe carrying capacity of ${fmt(carryingCapacityAU, 1)} AU — reduce head or add acreage.`
          : `Your ${fmt(acres, 1)} acres can safely support up to ${fmt(carryingCapacityHead, 1)} head for ${fmt(months, 1)} months.`,
      };
    },
    intro: "Overstocking a pasture depletes root reserves and can take years to recover from, while understocking wastes valuable land. This calculator applies the standard take-half-leave-half grazing rule and the Animal Unit Month (AUM) framework used by extension services to size your herd correctly.",
    sections: [
      { h: "What is an Animal Unit Month (AUM)?", body: "One AUM represents the forage a 1,000-lb beef cow with calf eats in one month — about 780 lbs of dry matter, or 26 lbs/day. Other livestock are converted to AU equivalents (a bull eats more, a sheep much less) so mixed herds can be sized on one scale." },
      { h: "Why only half the forage counts", body: "The 'take half, leave half' rule reserves the remaining plant material for photosynthesis, root regrowth, and soil cover. Grazing beyond that threshold stresses forage stands and accelerates erosion and weed invasion." },
      { h: "Reading your stocking density result", body: "Acres-per-animal-unit varies enormously by region — arid rangeland might need 20+ acres per AU while irrigated bottomland can support one AU per acre or less. Compare your result to local extension office benchmarks." },
      { h: "Using rotational grazing to increase capacity", body: "Subdividing pasture into paddocks and rotating livestock through them on a schedule can increase effective carrying capacity by 20-30% compared to continuous grazing, because plants get rest periods to regrow." },
    ],
    staticTable: {
      title: "Animal unit equivalents",
      head: ["Animal", "AU equivalent", "Approx. monthly DM intake"],
      rows: [
        ["Beef cow with calf", "1.0", "780 lbs"],
        ["Bull", "1.25", "975 lbs"],
        ["Horse", "1.2", "936 lbs"],
        ["Sheep", "0.2", "156 lbs"],
        ["Goat", "0.15", "117 lbs"],
        ["Yearling cattle", "0.6-0.7", "470-546 lbs"],
      ],
    },
    faqs: [
      { q: "How many cows can I graze on 20 acres?", a: "At 2,500 lbs DM/acre forage yield and a 6-month season, 20 acres yields roughly 6-7 cow-calf pairs under the take-half-leave-half rule — actual capacity depends heavily on your local forage yield." },
      { q: "What is dry matter (DM) forage production?", a: "Dry matter is forage weight with moisture removed, the standard unit for comparing feed value; extension offices publish regional DM yield estimates per acre by pasture type and rainfall." },
      { q: "Why only count half the available forage?", a: "Grazing more than half of standing forage depletes the plant's energy reserves needed for regrowth, leading to thinning stands, bare soil, and long-term pasture degradation." },
      { q: "How is a sheep's AU value only 0.2?", a: "Animal Unit values scale to a 1,000-lb cow's intake; a typical ewe weighs roughly 150-200 lbs and eats proportionally less, landing around 0.2 AU." },
      { q: "Does rotational grazing really increase carrying capacity?", a: "Yes, well-managed rotational systems typically boost usable forage yield 15-30% versus continuous grazing by allowing rest-and-regrowth periods between grazing passes." },
      { q: "What happens if I overgraze my pasture?", a: "Overgrazing weakens root systems, reduces future yield, invites weed and erosion problems, and can take one to several growing seasons to fully recover from." },
      { q: "How do I find my pasture's forage yield number?", a: "Contact your local agricultural extension office for regional forage production estimates, or clip and weigh a known square-foot sample of forage and scale it up." },
      { q: "Can I mix cattle and sheep on the same pasture calculation?", a: "Yes, convert each species to animal units using their respective AU factors, sum the totals, and compare against the same carrying capacity ceiling." },
      { q: "How long should a grazing season be for this calculator?", a: "Use the number of months you actually intend to graze continuously without supplemental feeding — commonly 4-7 months in temperate climates." },
      { q: "Does soil quality affect the AUM calculation?", a: "Indirectly — soil quality drives the forage yield (lbs DM/acre) input, so better soil and more rainfall should be reflected as a higher forage number." },
    ],
    formula: {
      expressions: [
        "UsableForage = Acres × ForageYield_lbs/acre × 0.50",
        "CarryingCapacity_AU = UsableForage / (780 × GrazingMonths)",
        "MaxHead = CarryingCapacity_AU / AU_Factor",
      ],
      variables: [
        { name: "780", meaning: "Standard AUM dry matter intake (lbs/month)" },
        { name: "AU_Factor", meaning: "Animal unit equivalent for species" },
      ],
    },
    related: ["horse-hay-feeding-calculator", "raised-bed-soil-calculator", "worm-composting-calculator"],
  },

  {
    slug: "worm-composting-calculator",
    cluster: "homestead",
    name: "Worm Composting Sizer",
    h1: "Vermicomposting Worm Population & Weekly Scrap Feeding Calculator",
    title: "Worm Composting Calculator: Bin Size & Feeding | Free (2026)",
    description: "Calculate worm colony size and bin surface area for vermicomposting instantly. Free calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "worm composting calculator",
      "how many pounds of red wigglers do i need",
      "vermicomposting bin size calculator",
      "worm bin feeding capacity calculator",
      "red wiggler worm ratio calculator",
      "vermiculture composting sizer",
    ],
    inputs: [
      { id: "scraps", label: "Weekly kitchen scraps", type: "number", def: "7", unit: "lbs/week", min: 0.5, step: 0.5 },
      { id: "length", label: "Bin length", type: "number", def: "24", unit: "in", min: 1, step: 1 },
      { id: "width", label: "Bin width", type: "number", def: "16", unit: "in", min: 1, step: 1 },
      { id: "species", label: "Worm species", type: "select", def: "red", options: [
        { value: "red", label: "Red Wigglers (Eisenia fetida)" },
        { value: "euro", label: "European Nightcrawlers" },
      ] },
    ],
    compute: (v) => {
      const scraps = n(v, "scraps"), length = n(v, "length"), width = n(v, "width"), species = s(v, "species");
      const wormLbsNeeded = scraps / 3.5;
      const minSurfaceSqFt = wormLbsNeeded * 1.0;
      const binSurfaceSqFt = (length * width) / 144;
      const capacityLbsWithBin = binSurfaceSqFt * 1.0;
      const weeklyCapacity = capacityLbsWithBin * 3.5;
      const speciesFactor = species === "euro" ? 0.85 : 1.0;
      const wormCount = wormLbsNeeded * (species === "euro" ? 250 : 1000) * speciesFactor;
      const binOK = binSurfaceSqFt >= minSurfaceSqFt;
      return {
        cards: [
          { label: "Worms needed", value: `${fmt(wormLbsNeeded, 2)} lbs`, sub: `~${fmt(wormCount, 0)} worms`, accent: true },
          { label: "Min bin surface area", value: `${fmt(minSurfaceSqFt, 2)} sq ft`, sub: "1 lb worms per sq ft" },
          { label: "Your bin surface area", value: `${fmt(binSurfaceSqFt, 2)} sq ft`, sub: binOK ? "Bin is large enough" : "⚠ Bin too small" },
          { label: "Weekly scrap capacity at this size", value: `${fmt(weeklyCapacity, 1)} lbs`, sub: "Max sustainable feeding rate" },
        ],
        table: {
          title: "Vermicomposting sizing",
          head: ["Metric", "Value"],
          rows: [
            ["Weekly kitchen scraps", `${fmt(scraps, 1)} lbs`],
            ["Worm pounds needed", fmt(wormLbsNeeded, 2)],
            ["Estimated worm count", fmt(wormCount, 0)],
            ["Minimum surface area", `${fmt(minSurfaceSqFt, 2)} sq ft`],
            ["Actual bin surface area", `${fmt(binSurfaceSqFt, 2)} sq ft`],
            ["Bin capacity (lbs scraps/week)", fmt(weeklyCapacity, 1)],
          ],
        },
        summary: binOK
          ? `Your bin can handle ${fmt(scraps, 1)} lbs/week of scraps with about ${fmt(wormLbsNeeded, 2)} lbs of worms.`
          : `Your bin is undersized — increase surface area to at least ${fmt(minSurfaceSqFt, 2)} sq ft to safely process ${fmt(scraps, 1)} lbs/week.`,
      };
    },
    intro: "Buying too few worms leads to rotting, smelly scraps piling up faster than they can be processed, while an oversized bin wastes space and bedding. This calculator sizes your starting worm colony and bin surface area to match your household's actual weekly food scrap volume.",
    sections: [
      { h: "The 50% body weight rule", body: "Healthy red wigglers eat roughly half their body weight in scraps per day, which works out to about 3.5 lbs of scraps processed weekly per pound of worms — the core ratio this calculator is built on." },
      { h: "Why surface area matters more than depth", body: "Worms feed near the surface where oxygen is available, so a wide, shallow bin processes more waste than a deep, narrow one of the same volume. The 1 lb of worms per square foot rule reflects that surface-feeding behavior." },
      { h: "Red Wigglers vs European Nightcrawlers", body: "Red Wigglers are the vermicomposting standard — voracious eaters that tolerate bin conditions well. European Nightcrawlers eat slightly less per pound but grow larger and double as fishing bait, so many keepers choose them for dual purposes." },
      { h: "What not to feed your worm bin", body: "Avoid citrus, onions, garlic, and meat/dairy in large quantities — acidic and pungent scraps can drive worms away from feeding zones or attract pests, while meat and dairy rot before worms can process them." },
    ],
    staticTable: {
      title: "Worm bin sizing reference",
      head: ["Weekly scraps", "Worms needed", "Min bin surface area"],
      rows: [
        ["3.5 lbs", "1.0 lb", "1.0 sq ft"],
        ["7 lbs", "2.0 lbs", "2.0 sq ft"],
        ["10.5 lbs", "3.0 lbs", "3.0 sq ft"],
        ["14 lbs", "4.0 lbs", "4.0 sq ft"],
        ["17.5 lbs", "5.0 lbs", "5.0 sq ft"],
        ["21 lbs", "6.0 lbs", "6.0 sq ft"],
      ],
    },
    faqs: [
      { q: "How many pounds of worms do I need for 5 lbs of scraps weekly?", a: "About 1.4 lbs of red wigglers, since worms process roughly 3.5 times their body weight in scraps per week (half their weight daily)." },
      { q: "How big should my worm bin be?", a: "Use 1 square foot of surface area per pound of worms in the colony — a 2x1 ft bin comfortably houses about 2 lbs of worms processing roughly 7 lbs of scraps weekly." },
      { q: "Can I overfeed my worm bin?", a: "Yes — excess uneaten food rots, creates odor, attracts pests, and can turn anaerobic; feed slightly less than the calculated capacity and add more once existing scraps are mostly gone." },
      { q: "How long does it take worms to multiply to fill a bin?", a: "Under good conditions, a worm population can double every 60-90 days, so starting with fewer worms than needed and letting them multiply naturally over a few months works for patient composters." },
      { q: "Do European Nightcrawlers eat as much as Red Wigglers?", a: "They eat somewhat less per pound of body weight but individually grow larger, so total colony processing capacity ends up broadly similar per pound of worms." },
      { q: "What bedding should I use in a worm bin?", a: "Shredded cardboard, newspaper, or coco coir moistened to a damp-sponge consistency works well as a carbon-rich base bedding layer." },
      { q: "Can I put citrus peels in my worm bin?", a: "In small amounts occasionally is fine, but large or frequent amounts of citrus can acidify the bin and repel worms — better to compost citrus separately." },
      { q: "How do I know if my bin is too small?", a: "Signs include persistent odor, uneaten food piling up, worms crawling up the sides or lid trying to escape, and slow decomposition — all indicate you've exceeded capacity." },
      { q: "Is vermicompost better than regular compost?", a: "Worm castings are more nutrient-dense and microbially active than standard compost, making them excellent as a soil amendment or potting mix additive in small quantities." },
      { q: "How often should I harvest worm castings?", a: "Most home bins are harvested every 3-6 months once the bedding has largely turned into dark, crumbly castings throughout." },
    ],
    formula: {
      expressions: [
        "WormLbsNeeded = WeeklyScraps_lbs / 3.5",
        "MinSurfaceArea_sqft = WormLbsNeeded × 1.0",
        "BinSurfaceArea_sqft = (Length_in × Width_in) / 144",
      ],
      variables: [
        { name: "3.5", meaning: "Weekly scraps processed per lb of worms" },
        { name: "1.0", meaning: "Sq ft surface area needed per lb of worms" },
      ],
    },
    related: ["raised-bed-soil-calculator", "pasture-grazing-aum-calculator", "beekeeping-sugar-syrup-calculator"],
  },

  {
    slug: "egg-incubation-hatch-calculator",
    cluster: "homestead",
    name: "Egg Incubation Hatch Date",
    h1: "Egg Incubation Hatch Date & Lock-Down Schedule Calculator",
    title: "Egg Incubation Hatch Date Calculator | Free (2026)",
    description: "Calculate exact hatch date and lock-down schedule for chicken, duck, goose, quail and turkey eggs instantly. Free calculator with a step-by-step breakdown.",
    keywords: [
      "egg incubation calculator hatch date",
      "chicken egg lockdown date calculator",
      "duck goose quail turkey incubation schedule",
      "when do incubated eggs hatch calculator",
      "egg candling schedule calculator",
      "incubator lockdown day calculator",
    ],
    inputs: [
      { id: "species", label: "Egg species", type: "select", def: "chicken", options: [
        { value: "chicken", label: "Chicken (21 days)" },
        { value: "duck", label: "Duck (28 days)" },
        { value: "goose", label: "Goose (30 days)" },
        { value: "quail", label: "Coturnix Quail (17 days)" },
        { value: "turkey", label: "Turkey (28 days)" },
      ] },
      { id: "startdate", label: "Incubation start date", type: "date", def: "2026-01-01" },
      { id: "airflow", label: "Incubator type", type: "select", def: "forced", options: [
        { value: "forced", label: "Forced-Air" },
        { value: "still", label: "Still-Air (+0.5°F needed, same duration)" },
      ] },
    ],
    compute: (v) => {
      const species = s(v, "species"), startdate = s(v, "startdate"), airflow = s(v, "airflow");
      const days: Record<string, number> = { chicken: 21, duck: 28, goose: 30, quail: 17, turkey: 28 };
      const totalDays = days[species] ?? 21;
      const start = new Date(startdate + "T00:00:00");
      const valid = !isNaN(start.getTime());
      const lockdown = new Date(start);
      lockdown.setDate(lockdown.getDate() + (totalDays - 3));
      const hatch = new Date(start);
      hatch.setDate(hatch.getDate() + totalDays);
      const candle1 = new Date(start);
      candle1.setDate(candle1.getDate() + 7);
      const candle2 = new Date(start);
      candle2.setDate(candle2.getDate() + 14);
      const fmtDate = (d: Date) => valid ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—";
      return {
        cards: [
          { label: "Estimated hatch date", value: fmtDate(hatch), sub: `Day ${totalDays}`, accent: true },
          { label: "Lock-down date", value: fmtDate(lockdown), sub: `Day ${totalDays - 3} — stop turning` },
          { label: "First candling", value: fmtDate(candle1), sub: "Day 7 — check fertility" },
          { label: "Second candling", value: fmtDate(candle2), sub: "Day 14 — check development" },
        ],
        table: {
          title: "Incubation schedule",
          head: ["Milestone", "Date", "Day"],
          rows: [
            ["Incubation start", fmtDate(start), "0"],
            ["First candling", fmtDate(candle1), "7"],
            ["Second candling", fmtDate(candle2), "14"],
            ["Lock-down (stop turning)", fmtDate(lockdown), String(totalDays - 3)],
            ["Estimated hatch", fmtDate(hatch), String(totalDays)],
          ],
        },
        summary: `${species.charAt(0).toUpperCase() + species.slice(1)} eggs (${totalDays} days, ${airflow === "still" ? "still-air" : "forced-air"} incubator) should hatch around ${fmtDate(hatch)}, with lock-down starting ${fmtDate(lockdown)}.`,
      };
    },
    intro: "Missing the lock-down window — when you stop turning eggs and raise humidity — is one of the most common reasons home hatches fail. This calculator takes your species and start date and produces the full milestone schedule: candling checks, lock-down, and estimated hatch day.",
    sections: [
      { h: "Why lock-down happens 3 days before hatch", body: "Chicks need to stop tumbling and orient themselves into hatching position in the final 72 hours. Turning during this window can cause malposition, so incubators are set to stop automatic turning and humidity is raised to help the membrane soften." },
      { h: "Candling milestones explained", body: "Day 7 candling reveals whether an egg is fertile (visible veining) or clear (infertile/early death) — clear eggs should be removed to avoid bacterial buildup. Day 14 candling confirms continued development and movement." },
      { h: "Still-air vs forced-air temperature differences", body: "Forced-air incubators circulate warm air evenly and run at about 99.5°F. Still-air incubators have a temperature gradient, so the thermometer should sit at egg-top height and run about 0.5-1°F warmer to compensate — but incubation length itself is unchanged." },
      { h: "Humidity by incubation phase", body: "Most species need roughly 45-55% relative humidity during development and a jump to 65-75% during lock-down to keep the hatching membrane pliable and prevent chicks from sticking to the shell." },
    ],
    staticTable: {
      title: "Incubation periods by species",
      head: ["Species", "Total days", "Lock-down day", "Humidity (dev / lockdown)"],
      rows: [
        ["Chicken", "21", "18", "45-50% / 65-70%"],
        ["Duck", "28", "25", "55% / 70-75%"],
        ["Goose", "30", "27", "55-60% / 70-75%"],
        ["Coturnix Quail", "17", "14", "45-50% / 65%"],
        ["Turkey", "28", "25", "50% / 65-70%"],
      ],
    },
    faqs: [
      { q: "What day should I stop turning chicken eggs?", a: "Day 18, three days before the expected day-21 hatch — this is the lock-down point where turning stops and humidity is raised." },
      { q: "Why haven't my eggs hatched on the exact predicted day?", a: "Hatch dates are estimates; chicks commonly hatch up to 24-48 hours before or after the calculated day depending on temperature consistency and genetics — avoid intervening too early." },
      { q: "Can I open the incubator during lock-down?", a: "Opening the incubator during lock-down causes rapid humidity loss that can shrink-wrap chicks to the membrane — resist opening until hatching is well underway or complete." },
      { q: "What if candling shows a clear egg at day 7?", a: "A clear egg with no visible veining is likely infertile or stopped developing very early and should be removed to prevent it from rotting or exploding in the incubator." },
      { q: "Does still-air incubation take longer than forced-air?", a: "No, the incubation period length is the same for both; only the exact temperature setting and thermometer placement differ." },
      { q: "How many days do quail eggs take to hatch?", a: "Coturnix quail eggs typically hatch in 17 days, with lock-down starting on day 14." },
      { q: "Why do goose and duck eggs take longer than chicken eggs?", a: "Larger eggs with more yolk and albumen simply require more time for the embryo to fully develop before hatching, roughly 28-30 days versus 21 for chicken." },
      { q: "What humidity should I use for lock-down?", a: "Most species benefit from raising humidity to 65-75% during the final 3 days to keep the internal membrane from drying out and trapping the chick." },
      { q: "Can I mix chicken and duck eggs in the same incubator?", a: "Yes, but you'll need to manage two separate turning/lockdown schedules and humidity needs, typically by using the duck schedule as the humidity baseline since it runs slightly higher." },
      { q: "How do I add these dates to my calendar?", a: "Note the lock-down and hatch dates from the table into your phone or calendar app as manual reminders soon after starting incubation so you don't miss the turning cutoff." },
    ],
    formula: {
      expressions: [
        "LockdownDate = StartDate + (TotalDays − 3)",
        "HatchDate = StartDate + TotalDays",
      ],
      variables: [
        { name: "TotalDays", meaning: "Species-specific incubation length" },
      ],
    },
    related: ["worm-composting-calculator", "pasture-grazing-aum-calculator", "reptile-uvb-distance-calculator"],
  },

  {
    slug: "horse-hay-feeding-calculator",
    cluster: "homestead",
    name: "Horse Hay Feeding",
    h1: "Horse Hay & Grain Daily Feeding Requirement Calculator",
    title: "Horse Hay Feeding Calculator | Free (2026)",
    description: "Calculate daily hay, flakes and dry matter needs for horses by body weight and workload instantly. Free calculator with a step-by-step breakdown.",
    keywords: [
      "horse hay feeding calculator",
      "how many flakes of hay per day for a horse",
      "horse daily forage requirement calculator",
      "dry matter intake calculator horse",
      "hay feeding by body weight calculator",
      "colic risk hay feeding calculator",
    ],
    inputs: [
      { id: "weight", label: "Horse body weight", type: "number", def: "1100", unit: "lbs", min: 100, step: 10 },
      { id: "workload", label: "Workload / activity level", type: "select", def: "maintenance", options: [
        { value: "maintenance", label: "Maintenance (2.0%)" },
        { value: "light", label: "Light Work (2.0-2.25%)" },
        { value: "moderate", label: "Moderate Work (2.25%)" },
        { value: "heavy", label: "Heavy Training (2.5%)" },
        { value: "lactating", label: "Lactating Broodmare (2.5-3.0%)" },
      ] },
      { id: "flakeweight", label: "Average flake weight", type: "number", def: "4", unit: "lbs", min: 0.5, step: 0.5 },
    ],
    compute: (v) => {
      const weight = n(v, "weight"), workload = s(v, "workload"), flakeweight = n(v, "flakeweight", 4);
      const factors: Record<string, number> = { maintenance: 0.02, light: 0.0225, moderate: 0.0225, heavy: 0.025, lactating: 0.028 };
      const factor = factors[workload] ?? 0.02;
      const dailyHay = weight * factor;
      const flakesPerDay = flakeweight > 0 ? dailyHay / flakeweight : 0;
      const minDM = weight * 0.015;
      const maxDM = weight * 0.025;
      const morning = flakesPerDay / 2;
      const evening = flakesPerDay / 2;
      const colicRisk = dailyHay < minDM;
      return {
        cards: [
          { label: "Daily forage needed", value: `${fmt(dailyHay, 1)} lbs`, sub: `${fmt(factor * 100, 2)}% of body weight`, accent: true },
          { label: "Flakes per day", value: fmt(flakesPerDay, 1), sub: `≈ ${fmt(morning, 1)} AM / ${fmt(evening, 1)} PM` },
          { label: "Min dry matter (1.5%)", value: `${fmt(minDM, 1)} lbs`, sub: "Absolute minimum for gut health" },
          { label: "Max dry matter (2.5%)", value: `${fmt(maxDM, 1)} lbs`, sub: colicRisk ? "⚠ Colic/ulcer risk — below minimum" : "Within healthy range" },
        ],
        table: {
          title: "Feeding breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Body weight", `${fmt(weight, 0)} lbs`],
            ["Workload category", workload],
            ["Intake factor", `${fmt(factor * 100, 2)}%`],
            ["Daily hay needed", `${fmt(dailyHay, 1)} lbs`],
            ["Flakes per day (AM/PM split)", `${fmt(flakesPerDay, 1)} (${fmt(morning, 1)}/${fmt(evening, 1)})`],
            ["Safe dry matter range", `${fmt(minDM, 1)}–${fmt(maxDM, 1)} lbs`],
          ],
        },
        summary: colicRisk
          ? `${fmt(dailyHay, 1)} lbs/day falls below the 1.5% minimum dry matter threshold — increase forage to reduce colic and ulcer risk.`
          : `Feed your ${fmt(weight, 0)}-lb horse ${fmt(dailyHay, 1)} lbs of hay daily, split into about ${fmt(flakesPerDay, 1)} flakes across morning and evening.`,
      };
    },
    intro: "Underfeeding forage is one of the leading preventable causes of colic and gastric ulcers in horses, since their digestive systems are built for near-constant grazing. This calculator sizes daily hay needs by body weight and workload, then converts the total into practical flakes per feeding.",
    sections: [
      { h: "Why forage percentage scales with workload", body: "A horse at maintenance needs about 2% of body weight in forage daily just to maintain gut function and weight, while heavy training or lactation raises energy demands enough to push that closer to 2.5-3%." },
      { h: "The 1.5% dry matter safety floor", body: "Veterinarians consider 1.5% of body weight in forage the absolute minimum to prevent gastric ulcers and colic from prolonged empty-stomach periods — this calculator flags any recommendation that falls below that floor." },
      { h: "Splitting feed across the day", body: "Horses evolved to graze 16-18 hours a day in small amounts, so splitting the daily total into at least two, ideally three or more, feedings reduces digestive stress compared to one or two large meals." },
      { h: "Grass hay vs alfalfa considerations", body: "Alfalfa is calorie- and protein-dense compared to grass hay, so horses on alfalfa may need a lower total weight of forage to meet the same energy needs — adjust workload category accordingly if switching hay types." },
    ],
    staticTable: {
      title: "Daily hay needs by weight and workload",
      head: ["Body weight", "Maintenance (2.0%)", "Moderate (2.25%)", "Heavy training (2.5%)"],
      rows: [
        ["900 lbs", "18 lbs", "20.3 lbs", "22.5 lbs"],
        ["1,000 lbs", "20 lbs", "22.5 lbs", "25 lbs"],
        ["1,100 lbs", "22 lbs", "24.8 lbs", "27.5 lbs"],
        ["1,200 lbs", "24 lbs", "27 lbs", "30 lbs"],
        ["1,400 lbs (draft)", "28 lbs", "31.5 lbs", "35 lbs"],
      ],
    },
    faqs: [
      { q: "How many flakes of hay should a 1,100 lb horse eat per day?", a: "At maintenance (2% of body weight) with 4-lb flakes, that's about 22 lbs of hay, or roughly 5.5 flakes split across at least two feedings." },
      { q: "What percentage of body weight should a horse eat in hay?", a: "A healthy range is 1.5-2.5% of body weight daily, with maintenance horses at the low end and heavily worked or lactating horses at the high end." },
      { q: "Can a horse eat too much hay?", a: "Free-choice access rarely causes problems for healthy horses since forage is low-calorie-dense relative to grain, but overweight or metabolic horses may need restricted or slow-feeder access." },
      { q: "What is the colic risk threshold this calculator flags?", a: "Feeding below 1.5% of body weight in forage daily significantly raises the risk of gastric ulcers and colic due to prolonged periods without gut-buffering saliva and fiber." },
      { q: "Should grain replace hay in the diet?", a: "No — grain should supplement, never replace, forage; horses need the bulk of their calories and nearly all their chew time to come from hay or pasture for digestive health." },
      { q: "How much does flake weight vary?", a: "Flake weight can range from 2 to 6 lbs depending on how tightly the hay was baled, so weighing a few flakes on a scale gives a far more accurate feeding calculation than guessing." },
      { q: "Do lactating mares really need 50% more forage?", a: "Lactating mares have very high energy and calcium demands for milk production, often needing 2.5-3% of body weight in quality forage plus additional concentrate feed." },
      { q: "Is alfalfa hay counted the same as grass hay here?", a: "This calculator estimates by weight regardless of hay type, but alfalfa is more calorie-dense, so horses on alfalfa may need a slightly lower total weight for the same energy intake." },
      { q: "How many feedings per day is ideal?", a: "Three or more small feedings, or free-choice access via slow feeders, more closely mimics natural grazing behavior than one or two large meals." },
      { q: "What body condition score should I target?", a: "Most healthy adult horses should sit around a 5 on the 9-point Henneke scale — ribs not visible but easily felt with light pressure." },
    ],
    formula: {
      expressions: [
        "DailyHay_lbs = BodyWeight_lbs × IntakeFactor",
        "FlakesPerDay = DailyHay_lbs / AvgFlakeWeight_lbs",
        "SafeRange = BodyWeight × [0.015, 0.025]",
      ],
      variables: [
        { name: "IntakeFactor", meaning: "0.02-0.03 depending on workload" },
      ],
    },
    related: ["pasture-grazing-aum-calculator", "raised-bed-soil-calculator", "reptile-uvb-distance-calculator"],
  },

  {
    slug: "reptile-uvb-distance-calculator",
    cluster: "homestead",
    name: "Reptile UVB Distance",
    h1: "Reptile UVB Lamp Distance & Ferguson Zone Index Calculator",
    title: "Reptile UVB Distance Calculator | Free (2026)",
    description: "Calculate safe UVB basking distance by Ferguson Zone and mesh obstruction instantly. Free calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "reptile uvb distance calculator",
      "ferguson zone uv index calculator",
      "bearded dragon uvb bulb distance",
      "t5 uvb mesh screen distance calculator",
      "leopard gecko uv index calculator",
      "metabolic bone disease uvb risk calculator",
    ],
    inputs: [
      { id: "species", label: "Reptile species", type: "select", def: "beardeddragon", options: [
        { value: "beardeddragon", label: "Bearded Dragon (Zone 3, UVI 3.0-6.0)" },
        { value: "leopardgecko", label: "Leopard Gecko (Zone 1, UVI 0-0.7)" },
        { value: "ballpython", label: "Ball Python (Zone 1, UVI 0-0.7)" },
        { value: "chameleon", label: "Chameleon (Zone 3, UVI 3.0-6.0)" },
        { value: "boxturtle", label: "Box Turtle (Zone 3, UVI 3.0-6.0)" },
      ] },
      { id: "lamp", label: "Lamp type", type: "select", def: "t5", options: [
        { value: "t5", label: "T5 HO Linear Tube (10.0/12.0)" },
        { value: "t8", label: "T8 Linear Tube" },
        { value: "coil", label: "Compact Coil" },
      ] },
      { id: "mesh", label: "Mesh screen obstruction", type: "select", def: "0", options: [
        { value: "0", label: "No screen (0%)" },
        { value: "30", label: "Standard mesh (30%)" },
        { value: "50", label: "Fine mesh (50%)" },
      ] },
    ],
    compute: (v) => {
      const species = s(v, "species"), lamp = s(v, "lamp"), mesh = n(v, "mesh");
      const zoneTarget: Record<string, { zone: string; uvi: number }> = {
        beardeddragon: { zone: "Zone 3", uvi: 4.5 },
        leopardgecko: { zone: "Zone 1", uvi: 0.4 },
        ballpython: { zone: "Zone 1", uvi: 0.4 },
        chameleon: { zone: "Zone 3", uvi: 4.5 },
        boxturtle: { zone: "Zone 3", uvi: 4.5 },
      };
      const target = zoneTarget[species] ?? zoneTarget.beardeddragon;
      const bulbRatedUVI: Record<string, number> = { t5: 12, t8: 7, coil: 5 };
      const bulbOutput = bulbRatedUVI[lamp] ?? 12;
      const effectiveOutput = bulbOutput * (1 - mesh / 100);
      const safeDistanceIn = target.uvi > 0 ? Math.sqrt(effectiveOutput / target.uvi) * 12 : 0;
      const meshAdjustedIn = safeDistanceIn;
      const mbdRisk = mesh >= 50 && lamp === "coil";
      return {
        cards: [
          { label: "Ferguson zone target", value: target.zone, sub: `UVI ${fmt(target.uvi, 1)} target`, accent: true },
          { label: "Effective bulb output", value: `UVI ${fmt(effectiveOutput, 1)}`, sub: `${mesh}% mesh loss applied` },
          { label: "Safe basking distance", value: `${fmt(safeDistanceIn, 1)} in`, sub: `${fmt(safeDistanceIn * 2.54, 1)} cm` },
          { label: "MBD risk", value: mbdRisk ? "⚠ High" : "Low", sub: mbdRisk ? "Weak bulb + fine mesh underdoses UVB" : "Setup should meet UVB needs" },
        ],
        table: {
          title: "UVB distance breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Species", species],
            ["Target Ferguson zone / UVI", `${target.zone} / ${fmt(target.uvi, 1)}`],
            ["Lamp type / rated UVI at 12in", `${lamp} / ${fmt(bulbOutput, 1)}`],
            ["Mesh obstruction", `${mesh}%`],
            ["Effective UVI output", fmt(effectiveOutput, 1)],
            ["Recommended distance", `${fmt(safeDistanceIn, 1)} in (${fmt(safeDistanceIn * 2.54, 1)} cm)`],
          ],
        },
        summary: `Place the ${lamp === "t5" ? "T5 HO" : lamp === "t8" ? "T8" : "compact coil"} bulb about ${fmt(safeDistanceIn, 1)} in from the basking spot to reach ${target.zone} (UVI ${fmt(target.uvi, 1)}) for this species.`,
      };
    },
    intro: "Metabolic bone disease from insufficient UVB exposure is one of the most common and preventable reptile health issues, yet bulb wattage alone tells you nothing about actual UV index at basking distance. This calculator applies the Ferguson Zone system and mesh screen loss to recommend a safe bulb distance.",
    sections: [
      { h: "What Ferguson Zones mean", body: "The Ferguson Zone system classifies reptiles by their natural UVB exposure needs, from Zone 1 (shade-dwelling, low UVI like ball pythons) to Zone 4 (full-sun baskers needing UVI 7.0+). Matching your species to its zone prevents both under- and over-exposure." },
      { h: "Why mesh screens matter so much", body: "Standard window screen mesh can block 30% or more of UVB output before it even reaches the animal, and fine pet-safety mesh can block up to 50%. Always measure basking distance from the bulb to the animal's back, not to the mesh, and account for that loss." },
      { h: "T5 HO vs T8 vs compact coil bulbs", body: "T5 High Output linear tubes produce the strongest, most even UVB output and are now the industry standard. T8 tubes are weaker and need closer placement. Compact coil bulbs are the least reliable, with uneven output and higher UVB overexposure risk to the eyes at close range — many keepers avoid them entirely." },
      { h: "Recalculating after any setup change", body: "Any change to bulb type, mesh, or even bulb age (UVB output degrades over 6-12 months even if visible light looks normal) should trigger a fresh distance calculation or a UV index meter reading if available." },
    ],
    staticTable: {
      title: "Ferguson Zone reference by species",
      head: ["Species", "Ferguson Zone", "Target UVI range"],
      rows: [
        ["Ball python, leopard gecko", "Zone 1", "0-0.7"],
        ["Corn snake, crested gecko", "Zone 1-2", "0.7-1.0"],
        ["Bearded dragon, chameleon", "Zone 3", "3.0-6.0"],
        ["Box turtle, uromastyx", "Zone 3", "2.6-7.4"],
        ["Full-sun basking lizards", "Zone 4", "6.0-9.0+"],
        ["Nocturnal geckos", "Zone 1", "0-0.5"],
      ],
    },
    faqs: [
      { q: "How far should a T5 10.0 UVB bulb be from a bearded dragon basking spot?", a: "With no mesh obstruction, roughly 12-18 inches typically achieves the Zone 3 target UVI of around 4.5 for bearded dragons, though always verify with a UV index meter if possible." },
      { q: "Does mesh screen really reduce UVB that much?", a: "Yes — standard mesh can cut UVB output by around 30%, and fine mesh by up to 50%, which is why the same bulb distance without a screen can underdose an animal once mesh is added." },
      { q: "What UVI does a leopard gecko need?", a: "As a Zone 1 crepuscular/nocturnal species, leopard geckos need very low UVB, typically UVI 0-0.7, often provided incidentally rather than through direct high-output basking bulbs." },
      { q: "How often should I replace my UVB bulb?", a: "Most linear T5/T8 UVB bulbs should be replaced every 6-12 months even if they still produce visible light, because UVB output degrades well before visible light output noticeably dims." },
      { q: "Can too much UVB hurt my reptile?", a: "Yes — excessive UVB exposure, especially from compact coil bulbs at close range, can cause photokeratoconjunctivitis (eye damage) and skin burns, so following zone-appropriate distances is important in both directions." },
      { q: "Is a UV index meter worth buying?", a: "Yes — a Solarmeter 6.5 or similar UV index meter gives an exact reading at basking distance and is far more reliable than calculated estimates once you own one." },
      { q: "Why are T5 bulbs recommended over T8?", a: "T5 High Output bulbs produce significantly stronger and more evenly distributed UVB across their length, allowing safer distances and better zone coverage than older T8 technology." },
      { q: "What Ferguson zone are chameleons?", a: "Most chameleon species are Zone 3, similar to bearded dragons, needing moderate-to-high UVB around UVI 3.0-6.0 at the basking spot." },
      { q: "Does glass or plastic block UVB?", a: "Yes, almost completely — regular glass and most plastics block the vast majority of UVB wavelengths, so UVB bulbs must shine through mesh or open air, never through glass or acrylic." },
      { q: "What are signs of metabolic bone disease from low UVB?", a: "Soft or bent limbs, lethargy, tremors, and a soft or swollen lower jaw are common signs of MBD from insufficient UVB/calcium metabolism and warrant an immediate vet visit." },
    ],
    formula: {
      expressions: [
        "EffectiveOutput_UVI = BulbRatedUVI × (1 − MeshLoss%)",
        "SafeDistance_in = sqrt(EffectiveOutput / TargetUVI) × 12",
      ],
      variables: [
        { name: "MeshLoss%", meaning: "UVB blocked by screen (0/30/50%)" },
        { name: "TargetUVI", meaning: "Species Ferguson Zone target UV index" },
      ],
    },
    related: ["egg-incubation-hatch-calculator", "horse-hay-feeding-calculator", "worm-composting-calculator"],
  },

  {
    slug: "firewood-cord-btu-calculator",
    cluster: "homestead",
    name: "Firewood Cord & BTU",
    h1: "Firewood Cord Volume, Weight & Seasoning BTU Heat Output Sizer",
    title: "Firewood Cord & BTU Calculator | Free (2026)",
    description: "Calculate firewood cords, face cords and available BTU heat output by species and moisture instantly. Free calculator with a step-by-step breakdown.",
    keywords: [
      "firewood cord calculator",
      "how many cords in a stack of firewood",
      "firewood btu calculator by species",
      "seasoned vs green wood btu loss calculator",
      "face cord to full cord conversion calculator",
      "creosote risk firewood moisture calculator",
    ],
    inputs: [
      { id: "length", label: "Stack length", type: "number", def: "8", unit: "ft", min: 0.1, step: 0.1 },
      { id: "width", label: "Stack width (log length)", type: "number", def: "1.33", unit: "ft", min: 0.1, step: 0.01, help: "Typical: 16in logs = 1.33 ft" },
      { id: "height", label: "Stack height", type: "number", def: "4", unit: "ft", min: 0.1, step: 0.1 },
      { id: "species", label: "Wood species", type: "select", def: "oak", options: [
        { value: "oak", label: "Oak (24 million BTU/cord dry)" },
        { value: "hickory", label: "Hickory (27.7 million BTU/cord dry)" },
        { value: "ash", label: "Ash (23.6 million BTU/cord dry)" },
        { value: "pine", label: "Pine (15 million BTU/cord dry)" },
        { value: "birch", label: "Birch (20.8 million BTU/cord dry)" },
      ] },
      { id: "moisture", label: "Moisture content", type: "select", def: "20", options: [
        { value: "50", label: "Green (50%)" },
        { value: "25", label: "Partially seasoned (25%)" },
        { value: "20", label: "Fully seasoned (<20%)" },
      ] },
    ],
    compute: (v) => {
      const length = n(v, "length"), width = n(v, "width"), height = n(v, "height");
      const species = s(v, "species"), moisture = n(v, "moisture");
      const baseBTU: Record<string, number> = { oak: 24, hickory: 27.7, ash: 23.6, pine: 15, birch: 20.8 };
      const dryBTU = baseBTU[species] ?? 24;
      const totalCuFt = length * width * height;
      const totalCords = totalCuFt / 128;
      const faceCords = totalCords * 3;
      const moistureFraction = moisture / 100;
      const availableBTU = dryBTU * (1 - moistureFraction * 1.25) * totalCords;
      const btuLossPct = moistureFraction * 1.25 * 100;
      const creosoteRisk = moisture >= 25;
      return {
        cards: [
          { label: "Total full cords", value: fmt(totalCords, 2), sub: `${fmt(totalCuFt, 0)} cu ft stacked`, accent: true },
          { label: "Face cords / ricks", value: fmt(faceCords, 2), sub: "Equivalent (16in log convention)" },
          { label: "Available BTU heat energy", value: `${fmt(Math.max(availableBTU, 0), 1)}M BTU`, sub: `${fmt(btuLossPct, 0)}% loss from moisture` },
          { label: "Creosote fire hazard", value: creosoteRisk ? "⚠ High" : "Low", sub: creosoteRisk ? "Season wood further before burning" : "Wood is dry enough to burn safely" },
        ],
        table: {
          title: "Firewood stack breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Stack dimensions", `${fmt(length, 1)} × ${fmt(width, 2)} × ${fmt(height, 1)} ft`],
            ["Total cubic feet", fmt(totalCuFt, 0)],
            ["Full cords (128 cu ft)", fmt(totalCords, 2)],
            ["Face cords (÷3)", fmt(faceCords, 2)],
            ["Species dry BTU/cord", `${fmt(dryBTU, 1)}M`],
            ["Moisture content", `${moisture}%`],
            ["Available BTU after moisture loss", `${fmt(Math.max(availableBTU, 0), 1)}M BTU`],
          ],
        },
        summary: creosoteRisk
          ? `This stack yields ${fmt(Math.max(availableBTU, 0), 1)} million BTU but ${moisture}% moisture creates elevated creosote/chimney fire risk — season further before burning.`
          : `This ${fmt(totalCords, 2)}-cord stack of ${species} yields approximately ${fmt(Math.max(availableBTU, 0), 1)} million BTU of usable heat.`,
      };
    },
    intro: "A cord of green oak and a cord of seasoned oak look identical stacked in the yard, but burning wet wood wastes a huge share of its heat energy boiling off trapped moisture and builds dangerous creosote in the chimney. This calculator converts stack dimensions into cords and adjusts BTU output for species and moisture content.",
    sections: [
      { h: "What is a full cord vs a face cord?", body: "A full cord is a stack measuring 4×4×8 feet, or 128 cubic feet of wood and air space. A face cord (or 'rick') is only as deep as one log length — typically 16 inches — so it takes three face cords of 16-inch logs to equal one full cord." },
      { h: "Why moisture destroys usable heat", body: "Green wood can be 50% water by weight; a huge share of the fire's energy goes into evaporating that water instead of heating your home. Fully seasoned wood under 20% moisture burns hotter, cleaner, and more efficiently." },
      { h: "Species BTU differences", body: "Dense hardwoods like hickory and oak pack far more energy per cord than softwoods like pine — hickory burns nearly twice as hot per cord as pine, though pine seasons and ignites faster, making it useful for kindling and shoulder-season fires." },
      { h: "Creosote and chimney fire risk", body: "Burning wood above 25% moisture produces cooler, smokier fires that deposit more creosote in the flue. Regular creosote buildup from wet wood is a leading cause of residential chimney fires — always season wood 6-12 months before burning." },
    ],
    staticTable: {
      title: "Dry BTU output per full cord by species",
      head: ["Species", "Million BTU/cord (dry)", "Seasoning time"],
      rows: [
        ["Hickory", "27.7", "12 months"],
        ["Oak", "24.0", "12-24 months"],
        ["Ash", "23.6", "6-12 months"],
        ["Birch", "20.8", "6-12 months"],
        ["Pine", "15.0", "6-9 months"],
        ["Maple", "22.0", "9-12 months"],
      ],
    },
    faqs: [
      { q: "How many full cords are in a 4x8 face cord stack?", a: "A face cord stacked 4 ft high, 16 in deep, 8 ft long is one-third of a full cord — you'd need three such stacks side by side to equal one full 128-cu-ft cord." },
      { q: "How much heat do I lose burning green wet firewood?", a: "At 50% moisture, this calculator applies roughly a 62.5% BTU reduction versus fully dry wood, meaning green wood delivers well under half the usable heat energy of properly seasoned wood." },
      { q: "How long should firewood season before burning?", a: "Most hardwoods need 6-12 months of covered, well-ventilated seasoning to drop below 20% moisture; dense woods like oak and hickory can take up to 18-24 months." },
      { q: "What moisture content is considered fully seasoned?", a: "Below 20% moisture content, measured with a firewood moisture meter, is generally considered safely seasoned and ready to burn efficiently." },
      { q: "Which firewood species produces the most heat per cord?", a: "Hickory and oak are among the highest-BTU common firewoods in North America, producing significantly more heat per cord than softwoods like pine or spruce." },
      { q: "How do I measure my firewood moisture content?", a: "Split a log fresh from the stack and press a moisture meter's pins into the freshly exposed inner face — surface readings are unreliable since the outside dries faster than the core." },
      { q: "What does the creosote warning mean?", a: "Wood above roughly 25% moisture burns cooler and smokier, depositing more creosote in your flue over time, which raises the risk of a dangerous chimney fire if not cleaned regularly." },
      { q: "Is a rick the same as a face cord?", a: "Yes, 'rick' and 'face cord' are regional terms for the same thing — a stack one log-length deep, as opposed to a full cord which is a standardized 4x4x8 ft volume." },
      { q: "How many cords does a typical household use per winter?", a: "Homes using wood as a primary heat source in a cold climate often burn 3-6 full cords per season, while occasional supplemental use might only need 1-2 cords." },
      { q: "Does log length affect the cord calculation?", a: "Yes — the width input represents the average log length in your stack, since face cord equivalence and stacked cubic footage both depend directly on that dimension." },
    ],
    formula: {
      expressions: [
        "TotalCords = (Length × Width × Height) / 128",
        "AvailableBTU = SpeciesDryBTU × (1 − Moisture% × 1.25) × TotalCords",
      ],
      variables: [
        { name: "SpeciesDryBTU", meaning: "Million BTU per full cord at <20% moisture" },
        { name: "Moisture%", meaning: "Fraction of wood weight that is water" },
      ],
    },
    related: ["beekeeping-sugar-syrup-calculator", "raised-bed-soil-calculator", "pasture-grazing-aum-calculator"],
  },

  {
    slug: "beekeeping-sugar-syrup-calculator",
    cluster: "homestead",
    name: "Beekeeping Sugar Syrup",
    h1: "Beekeeping Sugar Syrup Ratio Calculator: 1:1 Spring & 2:1 Fall Feed",
    title: "Beekeeping Sugar Syrup Calculator | Free (2026)",
    description: "Calculate exact sugar and water quantities for 1:1 spring or 2:1 fall bee syrup instantly. Free calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "beekeeping sugar syrup calculator",
      "1:1 vs 2:1 bee syrup ratio calculator",
      "how much sugar and water for bee feeding",
      "fall winter bee feeding syrup calculator",
      "spring stimulation feeding beehive calculator",
      "sugar water ratio for beehives calculator",
    ],
    inputs: [
      { id: "hives", label: "Number of beehives", type: "number", def: "2", min: 1, step: 1 },
      { id: "season", label: "Target feed season", type: "select", def: "spring", options: [
        { value: "spring", label: "Spring Stimulation (1:1 by weight)" },
        { value: "fall", label: "Autumn Winter Stores (2:1 by weight)" },
      ] },
      { id: "volume", label: "Desired total liquid volume", type: "number", def: "1", unit: "gal/hive", min: 0.1, step: 0.1 },
    ],
    compute: (v) => {
      const hives = n(v, "hives", 1), season = s(v, "season"), volumePerHive = n(v, "volume");
      const totalGallons = hives * volumePerHive;
      const totalQuarts = totalGallons * 4;
      const sugarPerQuart = season === "fall" ? 2.27 : 1.33;
      const totalSugarLbs = totalQuarts * sugarPerQuart;
      const finishedQuartsPerUnit = season === "fall" ? 0.88 : 0.75;
      const waterLbs = season === "fall" ? totalSugarLbs / 2 : totalSugarLbs;
      const waterQuarts = waterLbs / 2.086;
      const density = season === "fall" ? "Thick (2:1) — for winter stores" : "Thin (1:1) — for spring stimulation";
      return {
        cards: [
          { label: "Total sugar needed", value: `${fmt(totalSugarLbs, 1)} lbs`, sub: `${hives} hive${hives > 1 ? "s" : ""} × ${fmt(volumePerHive, 1)} gal`, accent: true },
          { label: "Water needed", value: `${fmt(waterQuarts, 1)} qt`, sub: `≈ ${fmt(waterLbs, 1)} lbs` },
          { label: "Finished syrup volume", value: `${fmt(totalQuarts, 1)} qt`, sub: `${fmt(totalGallons, 2)} gal total` },
          { label: "Syrup density", value: season === "fall" ? "2:1" : "1:1", sub: density },
        ],
        table: {
          title: "Syrup mixing breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Number of hives", String(hives)],
            ["Volume per hive", `${fmt(volumePerHive, 1)} gal`],
            ["Total finished syrup", `${fmt(totalGallons, 2)} gal (${fmt(totalQuarts, 1)} qt)`],
            ["Ratio", season === "fall" ? "2:1 sugar:water" : "1:1 sugar:water"],
            ["Total sugar", `${fmt(totalSugarLbs, 1)} lbs`],
            ["Total water", `${fmt(waterQuarts, 1)} qt (~${fmt(waterLbs, 1)} lbs)`],
          ],
        },
        summary: `Mix ${fmt(totalSugarLbs, 1)} lbs of white sugar with ${fmt(waterQuarts, 1)} qt of water to make ${fmt(totalGallons, 2)} gal of ${season === "fall" ? "2:1" : "1:1"} syrup for ${hives} hive${hives > 1 ? "s" : ""}.`,
      };
    },
    intro: "Feeding the wrong syrup ratio at the wrong time of year can stall spring buildup or leave a colony short on winter stores. This calculator sizes exact sugar and water quantities by weight for both 1:1 spring stimulation feed and 2:1 fall winter-stores feed, scaled to your number of hives.",
    sections: [
      { h: "1:1 spring syrup vs 2:1 fall syrup", body: "Thin 1:1 syrup mimics early-season nectar flow, encouraging the queen to ramp up brood production in spring. Thick 2:1 syrup packs more sugar per unit of water, letting bees convert and cap it as dense winter stores with less energy spent evaporating excess water." },
      { h: "Why measure by weight, not volume", body: "Sugar and water have different densities, so '1:1 by weight' is not the same as equal volumes — 1 lb of sugar takes up less space than 1 lb (pint) of water. Weighing on a kitchen scale gives far more consistent results than eyeballing cups." },
      { h: "Making the syrup safely", body: "Use hot (not boiling) water to fully dissolve sugar without caramelizing it, which can produce compounds harmful to bees. Let syrup cool to room temperature before feeding, and never use brown sugar, molasses, or other refined-sugar substitutes." },
      { h: "Emergency feeding timeline", body: "If a colony is found critically low on stores in fall, thick 2:1 syrup should be started immediately while temperatures still allow bees to process it — below about 50°F (10°C), bees struggle to take down and cure syrup before winter cluster formation." },
    ],
    staticTable: {
      title: "Sugar and water quantities by syrup type",
      head: ["Syrup type", "Sugar per gallon finished", "Typical use"],
      rows: [
        ["1:1 (spring)", "~5.3 lbs sugar", "Spring brood stimulation, package installs"],
        ["2:1 (fall)", "~9.1 lbs sugar", "Winter stores building"],
        ["1:1 quart", "~1.33 lbs sugar", "Small-batch feeding"],
        ["2:1 quart", "~2.27 lbs sugar", "Small-batch fall feeding"],
        ["1:1 5-gallon batch", "~26.5 lbs sugar", "Multi-hive apiary feeding"],
        ["2:1 5-gallon batch", "~45.5 lbs sugar", "Multi-hive fall feeding"],
      ],
    },
    faqs: [
      { q: "How much sugar and water do I need for 5 gallons of 2:1 fall syrup?", a: "Roughly 45.5 lbs of granulated white sugar dissolved into about 22.7 lbs (roughly 2.7 gallons) of hot water to produce 5 gallons of finished 2:1 syrup." },
      { q: "Why is 1:1 syrup used in spring and 2:1 in fall?", a: "Thin 1:1 syrup mimics natural nectar flow to stimulate brood rearing when the colony is building up, while thick 2:1 syrup gives bees a denser, easier-to-cure sugar source for building winter stores before cold weather sets in." },
      { q: "Can I use hot tap water to make bee syrup?", a: "Yes, hot tap water speeds dissolving, but avoid boiling the mixture on the stove for extended periods as prolonged heat can produce hydroxymethylfurfural (HMF), a compound toxic to bees in excess." },
      { q: "Should I add anything else to sugar syrup?", a: "Most beekeepers feed plain sugar syrup; some add a pinch of Honey-B-Healthy or essential oil supplements to stimulate feeding, but additives are optional and not required for basic nutrition." },
      { q: "How much syrup does one hive need in fall?", a: "Requirements vary by climate and colony size, but many beekeepers aim for 1-2 gallons of 2:1 syrup per week over several weeks in early fall to build adequate winter stores." },
      { q: "Is it too late to feed syrup if temperatures are below 50°F?", a: "Yes, bees struggle to take down, process, and cure syrup effectively below about 50°F (10°C), so switching to fondant or dry sugar feeding is recommended once temperatures drop that low." },
      { q: "Can I use brown sugar or molasses instead of white sugar?", a: "No — brown sugar, molasses, and raw sugars contain impurities and higher ash content that can cause dysentery in bees; only pure granulated white cane or beet sugar should be used." },
      { q: "How long does mixed syrup stay good?", a: "Syrup can ferment or grow mold within 1-2 weeks, especially in warm weather, so mix only what you'll use within that window and store any extra refrigerated." },
      { q: "What's the difference between weight ratio and volume ratio for syrup?", a: "Because sugar is denser per cup than water, a true 1:1 or 2:1 ratio by weight looks different from a 1:1 or 2:1 ratio by volume — this calculator uses the standard weight-based convention beekeepers reference." },
      { q: "How many hives can one 5-gallon batch of syrup feed?", a: "At roughly 1 gallon per hive per feeding round, a 5-gallon batch can supply 5 hives per round, though colony size and time of year affect exact consumption rates." },
    ],
    formula: {
      expressions: [
        "SugarLbs (1:1) = FinishedQuarts × 1.33",
        "SugarLbs (2:1) = FinishedQuarts × 2.27",
        "WaterLbs (2:1) = SugarLbs / 2",
      ],
      variables: [
        { name: "FinishedQuarts", meaning: "Total desired syrup volume in quarts" },
      ],
    },
    related: ["firewood-cord-btu-calculator", "worm-composting-calculator", "raised-bed-soil-calculator"],
  },
];
